import { lazy, Suspense, type ComponentType, type ReactNode } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { EcranPrevu } from '@/modules/accueil/EcranPrevu';
import { IntrouvablePage } from '@/modules/accueil/IntrouvablePage';
import { ConnexionPage } from '@/modules/connexion/ConnexionPage';
import { MotDePasseOubliePage } from '@/modules/connexion/MotDePasseOubliePage';
import { NouveauMotDePassePage } from '@/modules/connexion/NouveauMotDePassePage';
import { Chargement } from '@/shared/ui';
import { ExigerRole, ExigerSession, VersAccueil } from './gardes';
import { AppShell } from './layout/AppShell';
import { toutesLesEntrees } from './navigation';

/**
 * Chaque écran se charge à la demande : le partenaire sur son téléphone ne
 * télécharge ni la carte ni le planning, et l'écran de connexion s'affiche vite.
 */
function aLaDemande(charger: () => Promise<ComponentType>): ReactNode {
  const Ecran = lazy(() => charger().then((Composant) => ({ default: Composant })));
  return (
    <Suspense fallback={<Chargement plein={false} />}>
      <Ecran />
    </Suspense>
  );
}

/** Les écrans livrés. Toute autre entrée de navigation affiche son écran « prévu ». */
const ECRANS: Record<string, ReactNode> = {
  'mes-etapes': aLaDemande(() =>
    import('@/modules/terrain/MesEtapesPage').then((m) => m.MesEtapesPage)
  ),
  'mon-agenda': aLaDemande(() =>
    import('@/modules/terrain/MonAgendaPage').then((m) => m.MonAgendaPage)
  ),
  'ma-carte': aLaDemande(() => import('@/modules/terrain/MaCartePage').then((m) => m.MaCartePage)),
  'tickets-sav': aLaDemande(() =>
    import('@/modules/terrain/TicketsSavPage').then((m) => m.TicketsSavPage)
  ),
  'mes-documents': aLaDemande(() =>
    import('@/modules/terrain/MesDocumentsPage').then((m) => m.MesDocumentsPage)
  ),
  chantiers: aLaDemande(() =>
    import('@/modules/chantiers/ChantiersPage').then((m) => m.ChantiersPage)
  ),
  etudes: aLaDemande(() => import('@/modules/etudes/EtudesPage').then((m) => m.EtudesPage)),
  planning: aLaDemande(() => import('@/modules/planning/PlanningPage').then((m) => m.PlanningPage)),
  sav: aLaDemande(() => import('@/modules/sav/SavPage').then((m) => m.SavPage)),
  'a-traiter': aLaDemande(() =>
    import('@/modules/a-traiter/ATraiterPage').then((m) => m.ATraiterPage)
  ),
  carte: aLaDemande(() => import('@/modules/carte/CartePage').then((m) => m.CartePage)),
  entreprises: aLaDemande(() =>
    import('@/modules/entreprises/EntreprisesPage').then((m) => m.EntreprisesPage)
  ),
  partenaires: aLaDemande(() =>
    import('@/modules/partenaires/PartenairesPage').then((m) => m.PartenairesPage)
  ),
  utilisateurs: aLaDemande(() =>
    import('@/modules/utilisateurs/UtilisateursPage').then((m) => m.UtilisateursPage)
  ),
  chaine: aLaDemande(() => import('@/modules/chaine/ChainePage').then((m) => m.ChainePage)),
  journal: aLaDemande(() => import('@/modules/journal/JournalPage').then((m) => m.JournalPage)),
  courriels: aLaDemande(() =>
    import('@/modules/courriels/CourrielsPage').then((m) => m.CourrielsPage)
  ),
  import: aLaDemande(() => import('@/modules/import/ImportPage').then((m) => m.ImportPage)),
  factures: aLaDemande(() =>
    import('@/modules/factures/MesFacturesPage').then((m) => m.MesFacturesPage)
  ),
  incidents: aLaDemande(() =>
    import('@/modules/incidents/IncidentsPage').then((m) => m.IncidentsPage)
  ),
  guide: aLaDemande(() => import('@/modules/guide/GuidePage').then((m) => m.GuidePage)),
  aide: aLaDemande(() => import('@/modules/aide/AidePage').then((m) => m.AidePage)),
};

/** Les fiches, sous leur liste : `/entreprises/:id`. */
const FICHES: Record<string, ReactNode> = {
  'mes-etapes': aLaDemande(() =>
    import('@/modules/terrain/EtapeTerrainPage').then((m) => m.EtapeTerrainPage)
  ),
  chantiers: aLaDemande(() =>
    import('@/modules/chantiers/FicheChantierPage').then((m) => m.FicheChantierPage)
  ),
  sav: aLaDemande(() => import('@/modules/sav/FicheTicketPage').then((m) => m.FicheTicketPage)),
  entreprises: aLaDemande(() =>
    import('@/modules/entreprises/FicheEntreprisePage').then((m) => m.FicheEntreprisePage)
  ),
  partenaires: aLaDemande(() =>
    import('@/modules/partenaires/FichePartenairePage').then((m) => m.FichePartenairePage)
  ),
};

export const routes: RouteObject[] = [
  { path: '/connexion', element: <ConnexionPage /> },
  { path: '/mot-de-passe-oublie', element: <MotDePasseOubliePage /> },
  { path: '/nouveau-mot-de-passe', element: <NouveauMotDePassePage /> },
  /* Les pages légales se lisent sans compte. */
  {
    path: '/mentions-legales',
    element: aLaDemande(() =>
      import('@/modules/legal/MentionsLegalesPage').then((m) => m.MentionsLegalesPage)
    ),
  },
  {
    path: '/confidentialite',
    element: aLaDemande(() =>
      import('@/modules/legal/ConfidentialitePage').then((m) => m.ConfidentialitePage)
    ),
  },
  {
    path: '/',
    element: (
      <ExigerSession>
        <AppShell />
      </ExigerSession>
    ),
    children: [
      { index: true, element: <VersAccueil /> },
      ...toutesLesEntrees().flatMap((entree) => {
        const fiche = FICHES[entree.id];
        return [
          {
            path: entree.chemin.slice(1),
            element: (
              <ExigerRole roles={entree.roles} ceoSeul={entree.ceoSeul}>
                {ECRANS[entree.id] ?? <EcranPrevu entree={entree} />}
              </ExigerRole>
            ),
          },
          ...(fiche
            ? [
                {
                  path: `${entree.chemin.slice(1)}/:id`,
                  element: (
                    <ExigerRole roles={entree.roles} ceoSeul={entree.ceoSeul}>
                      {fiche}
                    </ExigerRole>
                  ),
                },
              ]
            : []),
        ];
      }),
      { path: '*', element: <IntrouvablePage /> },
    ],
  },
];

export function creerRouteur() {
  return createBrowserRouter(routes);
}
